import type { ReactElement } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { InvoiceDetails, Subscription } from 'api-client/models';

interface Props {
  subscription: Subscription;
  invoice: InvoiceDetails | null;
  pending: boolean;
  onManage: () => void;
  timeZone?: string;
}

const dateLabel = (date: Date | null | undefined, timeZone?: string): string =>
  date
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeZone,
      }).format(date)
    : 'Not available';

export default function BillingSummary({ subscription, invoice, pending, onManage, timeZone }: Props): ReactElement {
  const attention = subscription.cancelAtPeriodEnd || subscription.status === 'past_due';
  return (
    <Card component="section" aria-labelledby="billing-current-title">
      <CardContent>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Stack spacing={1}>
              <Typography id="billing-current-title" component="h2" variant="h5">
                {subscription.displayName}
              </Typography>
              <Chip
                color={attention ? 'warning' : subscription.isTrialing ? 'info' : 'success'}
                variant="outlined"
                label={subscription.isTrialing ? 'Trial' : subscription.status.replace(/_/g, ' ')}
                sx={{ alignSelf: 'flex-start' }}
              />
            </Stack>
            <Button variant="contained" loading={pending} disabled={pending} onClick={onManage}>
              Manage billing
            </Button>
          </Stack>
          <Typography>
            {subscription.billingInterval} · Billing currency: {subscription.currency?.toUpperCase() || 'Not available'}
          </Typography>
          {subscription.isTrialing && (
            <Alert severity="info">Your trial ends on {dateLabel(subscription.trialEnd, timeZone)}.</Alert>
          )}
          {subscription.cancelAtPeriodEnd && (
            <Alert severity="warning">
              Your subscription is scheduled to end on {dateLabel(subscription.currentPeriodEnd, timeZone)}.
            </Alert>
          )}
          {subscription.status === 'past_due' && (
            <Alert severity="warning">Your payment is past due. Manage billing to update your payment details.</Alert>
          )}
          <TableContainer tabIndex={0} role="region" aria-label="Subscription items" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 320 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Listed price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscription.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.price.productName}</TableCell>
                    <TableCell>{item.quantity ?? 'Not specified'}</TableCell>
                    <TableCell>{item.price.paymentAmount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack spacing={1}>
            <Typography component="h3" variant="h6">
              Upcoming invoice
            </Typography>
            {invoice ? (
              <>
                <Typography component="p" variant="h5">
                  {invoice.totalDisplay}
                </Typography>
                <Typography color="text.secondary">
                  Billing period ends {dateLabel(invoice.periodEnd, 'UTC')}.
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary">
                Upcoming invoice information is not available for this subscription.
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
